import React from 'react';
import { InventoryItem } from './equipmentTypes';
import { getSlotName } from './equipmentUtils';

interface EquipmentItemListProps {
  inventory: InventoryItem[];
  consumables: InventoryItem[];
  slotFilter: string;
  onSlotFilterChange: (filter: string) => void;
  onDragStart: (item: InventoryItem) => void;
  onConsumeItem: (itemId: number) => void;
}

const EquipmentItemList: React.FC<EquipmentItemListProps> = ({
  inventory,
  consumables,
  slotFilter,
  onSlotFilterChange,
  onDragStart,
  onConsumeItem,
}) => {
  const filtered = inventory.filter(item => {
    if (slotFilter === 'all') return true;
    const itemSlotName = getSlotName(item.equipmentSlot);
    if (slotFilter === 'Ring1' || slotFilter === 'Ring2') {
      return itemSlotName === 'Ring1' || itemSlotName === 'Ring2';
    }
    return itemSlotName === slotFilter;
  });

  return (
    <div className="inventory-panel">
      <h3>Inventory ({inventory.length} items)</h3>
      <p className="inventory-hint">Drag items to equipment slots</p>

      <div style={{ marginBottom: '10px' }}>
        <label style={{ color: 'white', marginRight: '10px' }}>Filter by slot:</label>
        <select
          value={slotFilter}
          onChange={(e) => onSlotFilterChange(e.target.value)}
          style={{ padding: '5px 10px', borderRadius: '4px', backgroundColor: '#333', color: 'white', border: '1px solid #555' }}
        >
          <option value="all">All Slots</option>
          <option value="MainHand">Main Hand</option>
          <option value="OffHand">Off Hand</option>
          <option value="Head">Head</option>
          <option value="Chest">Chest</option>
          <option value="Legs">Legs</option>
          <option value="Feet">Feet</option>
          <option value="Hands">Hands</option>
          <option value="Necklace">Necklace</option>
          <option value="Ring1">Ring 1</option>
          <option value="Ring2">Ring 2</option>
          <option value="Belt">Belt</option>
          <option value="Back">Back</option>
        </select>
      </div>

      <div className="inventory-grid">
        {filtered.map(item => (
          <div
            key={item.id}
            className="inventory-item"
            draggable
            onDragStart={() => onDragStart(item)}
            style={{ padding: '8px', margin: '4px', backgroundColor: 'rgba(0, 0, 0, 0.3)', border: '1px solid #555', borderRadius: '4px', cursor: 'grab' }}
          >
            <div className="item-name" style={{ fontWeight: 'bold', marginBottom: '4px' }}>{item.itemName}</div>
            {item.requiredClass && (
              <div style={{ fontSize: '0.85em', color: '#90CAF9', marginBottom: '4px' }}>
                {item.requiredClass} Lv.{item.requiredLevel}
              </div>
            )}
            {item.isTwoHanded && <div className="two-handed-badge">⚔️ 2H</div>}
            {(item.attackBonus > 0 || item.defenseBonus > 0 || item.hpBonus > 0 || item.speedBonus > 0) && (
              <div className="item-stats" style={{ fontSize: '0.85em', marginTop: '4px' }}>
                {item.attackBonus > 0 && <span style={{ color: '#F44336' }}>⚔️ +{item.attackBonus}</span>}
                {item.defenseBonus > 0 && <span style={{ color: '#2196F3' }}>🛡️ +{item.defenseBonus}</span>}
                {item.hpBonus > 0 && <span style={{ color: '#4CAF50' }}>❤️ +{item.hpBonus}</span>}
                {item.speedBonus > 0 && <span style={{ color: '#FF9800' }}>⚡ +{item.speedBonus}</span>}
              </div>
            )}
            {(item.minDamage || item.maxDamage) && (
              <div style={{ fontSize: '0.85em', marginTop: '6px', padding: '4px', backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: '3px' }}>
                <div style={{ color: '#FFB74D', fontWeight: 'bold' }}>
                  ⚔️ {item.minDamage || 0}-{item.maxDamage || 0} {item.damageType || 'Physical'}
                </div>
                {(item.minRange || item.maxRange) && (
                  <div style={{ color: '#81C784' }}>📏 Range: {item.minRange || 1}-{item.maxRange || 1}</div>
                )}
                {item.weaponAPCost && item.weaponAPCost > 1 && (
                  <div style={{ color: '#64B5F6' }}>⚡ {item.weaponAPCost} AP</div>
                )}
                {item.aoeShape && item.aoeShape !== 'Single' && (
                  <div style={{ color: '#FF5722' }}>💥 {item.aoeShape}</div>
                )}
                {item.usesAmmo && (
                  <div style={{ color: item.currentAmmo === 0 ? '#F44336' : '#FFC107' }}>
                    🎯 {item.currentAmmo || 0}/{item.maxAmmo || 0} ammo
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="no-inventory">
            {slotFilter === 'all' ? 'No equipment items in inventory' : `No ${slotFilter} items in inventory`}
          </div>
        )}
      </div>

      {consumables.length > 0 && (
        <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(76, 175, 80, 0.1)', borderRadius: '8px', border: '1px solid #4caf50' }}>
          <h4 style={{ color: '#4caf50', marginTop: 0 }}>🧪 Consumables ({consumables.length})</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {consumables.map(item => (
              <div
                key={item.id}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '6px', border: '1px solid #555' }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', color: '#fff' }}>{item.itemName}</div>
                  {item.itemName.includes('Arrow') && (
                    <div style={{ fontSize: '0.85em', color: '#aaa', marginTop: '4px' }}>
                      Refills ammunition for ranged weapons
                    </div>
                  )}
                </div>
                <button
                  onClick={() => onConsumeItem(item.id)}
                  style={{ padding: '8px 16px', background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', transition: 'background 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#45a049'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#4caf50'}
                >
                  Use
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentItemList;
